import { useCallback, useState } from "react";

import type { InputMaybe, LangCode } from "@/lib/gql/__generated__/graphql";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog/dialog";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";
import { TextAreaField } from "@/components/ui/form/fields/text-area-field";
import Send from "@/components/ui/icon/send";

import type { ComponentProps } from "react";

type Props = {
	/** プレビューボタン(「テスト送信」)を表示しないかどうか */
	hidePreviewButton?: boolean;

	label: string;
	dialogTitle: string;

	value: InputMaybe<string> | undefined;
	errorMessage: string | undefined;
	handleChange: (input: string) => void;
	sendTestEmail: ({ lang, email }: { lang: LangCode; email: string }) => Promise<void>;
	lang: LangCode;
	hotelEmailAddress: string;
};

export const QuestionEmailTemplateItem = ({
	hidePreviewButton = false,
	label,
	value,
	errorMessage,
	handleChange,
	sendTestEmail,
	dialogTitle,
	lang,
	hotelEmailAddress,
}: Props) => {
	const [emailAddress, setEmailAddress] = useState<string>(hotelEmailAddress);
	const handleEmailAddressChange = useCallback((input: string) => {
		setEmailAddress(input);
	}, []);

	const [open, setOpen] = useState(false);

	const handleSendTestEmail = useCallback(async () => {
		await sendTestEmail({
			lang,
			email: emailAddress,
		});
	}, [emailAddress, lang, sendTestEmail]);

	const buttonVariant: ComponentProps<typeof Button>["variant"] = emailAddress ? "default" : "disabled";

	const handleClickPreviewButton = useCallback(() => {
		setOpen(true);
	}, []);

	return (
		<>
			<div className="space-y-3">
				<div className="flex items-end justify-between">
					<p className="text-sm ">{label}</p>

					{!hidePreviewButton && (
						<Button
							variant={"outline"}
							type={"button"}
							onClick={handleClickPreviewButton}
							icon={<Send size="sm" />}
						>
							テスト送信
						</Button>
					)}
				</div>
				<TextAreaField
					value={value ?? undefined}
					handleChange={handleChange}
					errorMessage={errorMessage}
					placeholder="オリジナルの文面を追加したい場合はこちらに記入をしてください。送られる文面は右上の「テスト送信」から確認できます。"
				/>
			</div>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="space-y-4">
					<DialogHeader>
						<DialogTitle>{dialogTitle}に送られるメールのテスト送信</DialogTitle>
						<DialogDescription className="text-xs">
							{/* テキストボックスに入力されているメールテンプレートの内容が上記のアドレス宛に送信されます。 */}
							{/* 確認後は必ず、右上の「保存」もしくは「更新」ボタンを押して、内容を確定させてください。*/}
							文言を追加していない場合も、デフォルトのメール内容を確認いただけます
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-8">
						<InputTextField
							label="送信先メールアドレス"
							value={emailAddress}
							handleChange={handleEmailAddressChange}
							errorMessage={undefined}
						/>
						<div className="flex justify-end">
							<Button type="button" size="lg" variant={buttonVariant} onClick={handleSendTestEmail}>
								送信
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
};

QuestionEmailTemplateItem.displayName = "QuestionEmailTemplateItem";
